"use client";

import { useState, useEffect, useMemo } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Bot, BookOpen, Brain, Cpu, Timer, Wrench } from "lucide-react";
import { patch, fetchData } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { toast } from "sonner";
import { FormField } from "@/components/shared/form-fields";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CommandAgent } from "@/types/command-agent";
import { PermissionGate } from "@/components/shared/permission-gate";
import { VoiceSlider } from "@/components/shared/voice-slider";
import { AppList } from "@/components/shared/app-list";
import { ConnectedAppsActions } from "@/components/shared/connected-apps-actions";
import { DataSourcesList } from "@/components/shared/datasources-list";
import { DataTableIntegrationSelector } from "@/components/data-table-integrations/DataTableIntegrationSelector";
import { ConnectedDataTableIntegrations } from "@/components/data-table-integrations/ConnectedDataTableIntegrations";
import {
    mergeDataTableIntegrationsSelection,
    normalizeDataTableIntegrationsForCompare,
    sanitizeDataTableIntegrations,
} from "@/components/data-table-integrations/constants";
import { CommandAgentSharingCard } from "@/components/command-team/command-agent/cards/command-agent-sharing-card";
import { isSystemCommandAgent, sameRoleIdLists } from "@/components/command-team/command-agent/utils/command-agent-sharing-utils";
import {
    CommandAgentSettingsActionBar,
    getCommandAgentSettingsPanelsWithErrors,
    type CommandAgentSettingsPanelId,
} from "@/components/command-team/command-agent/utils/command-agent-settings-layout";
import { applyLlmProviderChange, getDefaultModelForProvider } from "@/components/command-team/command-agent/utils/command-agent-llm-utils";
import { CommandV2ExpiredConnectionsBanner } from "@/components/command-team-v2/cards/command-v2-expired-connections-banner";
import { useCommandAgentExpiredIntegrations } from "@/hooks/use-command-agent-expired-integrations";
import { SettingsPanelNav } from "@/components/shared/settings-panel-nav";
import { SettingsPanelCard } from "@/components/shared/settings-panel-card";
import { AgentKnowledgeBaseTab } from "@/components/shared/agent-knowledge-base-tab";
import type { SettingsNavGroup } from "@/types/settings-nav";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

interface RolesListResponse {
    items?: Array<{ id?: string; title?: string }>;
}

interface CommandAgentSettingsTabProps {
    agent: CommandAgent;
}

interface FormErrors extends Record<string, string | undefined> {
    name: string;
    description: string;
    role: string;
    persona: string;
    restrictions: string;
    llm_provider: string;
    llm_model: string;
    llm_timeout: string;
    llm_temperature: string;
    llm_max_tokens: string;
    llm_max_completion_tokens: string;
    app_integrations?: string;
    custom_apps?: string;
    datasources?: string;
    data_table_integrations?: string;
    visibility: string;
    shared_roles: string;
}

interface LLMModel {
    provider: string;
    model_key: string;
    model_id: string;
    display_name: string;
    context_window: number;
    default_max_tokens: number;
    max_tokens_min: number;
    max_tokens_max: number;
    default_temperature: number;
    temperature_min: number;
    temperature_max: number;
    supports_streaming: boolean;
    supports_tools: boolean;
}

interface LLMModelsResponse {
    models: LLMModel[];
    total: number;
}

// Helper function to safely convert number to string with default
const toNumberString = (value: number | null | undefined, defaultValue: string): string => {
    return value !== null && value !== undefined ? value.toString() : defaultValue;
};

function buildCommandAgentFormState(
    agent: CommandAgent,
    models: LLMModel[]
) {
    const agentModel = models.find(
        (model) => model.model_id === agent.llm_model || model.model_key === agent.llm_model
    );
    const provider = agent.llm_provider || agentModel?.provider || "OPENAI";
    const normalizedModelKey = agentModel?.model_key || agent.llm_model || "";

    const datasources = (agent.datasources ?? []).map((ds: string | { id?: string }) =>
        typeof ds === "string" ? ds : ds.id || String(ds)
    );

    return {
        name: agent.name || "",
        description: agent.description || "",
        role: agent.role || "",
        persona: agent.persona || "",
        restrictions: agent.restrictions || "",
        llm_provider: provider,
        llm_model: normalizedModelKey,
        llm_timeout: toNumberString(agent.llm_timeout, "10"),
        llm_temperature: toNumberString(
            agent.llm_temperature,
            agentModel?.default_temperature?.toString() || "0"
        ),
        llm_max_tokens: toNumberString(
            agent.llm_max_tokens,
            agentModel?.default_max_tokens?.toString() || ""
        ),
        app_integrations: (agent.app_integrations ?? []).map((app) => app.integration_id),
        custom_apps: (agent.custom_apps ?? []).map((app) => app.custom_app_id),
        datasources,
        data_table_integrations: sanitizeDataTableIntegrations(agent.data_table_integrations),
        visibility: agent.visibility ?? "PERSONAL",
        shared_roles: [...(agent.shared_roles ?? [])],
    };
}

function cloneCommandAgentFormState(state: ReturnType<typeof buildCommandAgentFormState>) {
    return {
        ...state,
        datasources: [...state.datasources],
        app_integrations: [...state.app_integrations],
        custom_apps: [...state.custom_apps],
        data_table_integrations: [...state.data_table_integrations],
        shared_roles: [...state.shared_roles],
    };
}

export function CommandAgentSettingsTab({ agent }: CommandAgentSettingsTabProps) {
    const queryClient = useQueryClient();
    const isSystemAgent = isSystemCommandAgent(agent);
    const [panel, setPanel] = useState<CommandAgentSettingsPanelId>("general");
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState<any>(null);
    const [previousModel, setPreviousModel] = useState<string>("");
    const [focusedDataTableId, setFocusedDataTableId] = useState<string | null>(
        null
    );

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        llm_provider: "",
        llm_model: "",
        llm_timeout: "",
        llm_temperature: "",
        llm_max_tokens: "",
        app_integrations: [] as string[],
        custom_apps: [] as string[],
        datasources: [] as string[],
        data_table_integrations: [] as DataTableIntegration[],
        visibility: "PERSONAL" as AppIntegrationVisibility,
        shared_roles: [] as string[],
    });

    const [errors, setErrors] = useState<FormErrors>({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        llm_provider: "",
        llm_model: "",
        llm_timeout: "",
        llm_temperature: "",
        llm_max_tokens: "",
        llm_max_completion_tokens: "",
        app_integrations: "",
        custom_apps: "",
        datasources: "",
        data_table_integrations: "",
        visibility: "",
        shared_roles: "",
    });

    // Fetch LLM models
    const { data: llmModelsData, isLoading: isLoadingModels } = useQuery<LLMModelsResponse>({
        queryKey: ["llm-models"],
        queryFn: () => fetchData("/api/command-team/llm-config/models"),
    });

    const { data: rolesQueryData, isLoading: rolesLoading, hasPermission: canListRoles } =
        useProtectedQuery<RolesListResponse | Array<{ id?: string; title?: string }>>({
            queryKey: ["command-agent-role-options-settings-tab", agent?.id ?? ""],
            requiredPermissions: ["LIST_ROLES"],
            queryFn: async () => {
                const params = new URLSearchParams({ page: "1", limit: "500" });
                return fetchData(`/api/rbac/roles?${params.toString()}`);
            },
            enabled: Boolean(agent?.id),
        });

    const roleOptions = useMemo(() => {
        const raw = rolesQueryData;
        const items = Array.isArray(raw) ? raw : raw?.items ?? [];
        return items
            .filter((r): r is { id: string; title?: string } => Boolean(r.id))
            .map((r) => ({ label: r.title?.trim() || r.id, value: r.id }));
    }, [rolesQueryData]);

    // Get models filtered by provider
    const filteredModels = useMemo(() => {
        if (!llmModelsData?.models) return [];
        if (!formData.llm_provider) return [];
        return llmModelsData.models.filter((model) => model.provider === formData.llm_provider);
    }, [llmModelsData?.models, formData.llm_provider]);

    // Get selected model config
    const selectedModel = useMemo(() => {
        if (!formData.llm_model || !llmModelsData?.models) return null;
        return llmModelsData.models.find(
            (model) => model.model_key === formData.llm_model || model.model_id === formData.llm_model
        );
    }, [formData.llm_model, llmModelsData?.models]);

    const agentConnectionIds = useMemo(
        () => ({
            app_integrations: (agent.app_integrations ?? []).map((app) => app.integration_id),
            custom_apps: (agent.custom_apps ?? []).map((app) => app.custom_app_id),
        }),
        [agent]
    );

    // Map agent datasources to datasource IDs for DataSourcesList component
    const getDataSourceIdsFromAgent = useMemo(() => {
        if (!agent?.datasources) return [];
        // Handle both string array and object array formats
        return agent.datasources.map((ds: any) => {
            if (typeof ds === "string") return ds;
            return ds.id || ds;
        });
    }, [agent?.datasources]);

    const getDataTableIdsFromAgent = useMemo(() => {
        if (!agent.data_table_integrations?.length) return [];
        return agent.data_table_integrations.map(
            (integration) => integration.data_table_id
        );
    }, [agent.data_table_integrations]);

    // Initialize form data from agent — keyed on updated_at so a save+refetch re-syncs.
    useEffect(() => {
        if (hasUnsavedChanges) return;
        if (!agent?.id || !llmModelsData?.models) return;

        const initialData = buildCommandAgentFormState(agent, llmModelsData.models);

        setFormData(initialData);
        setOriginalFormData(cloneCommandAgentFormState(initialData));
        setPreviousModel(initialData.llm_model);
        setHasUnsavedChanges(false);
    }, [agent, agent.updated_at, llmModelsData?.models]);

    // Handle model changes: update max_tokens and temperature when model changes
    useEffect(() => {
        if (!formData.llm_model || !selectedModel) return;

        // Model changed - update defaults
        if (formData.llm_model !== previousModel) {
            setFormData((prev) => ({
                ...prev,
                llm_max_tokens: selectedModel.default_max_tokens.toString(),
                llm_temperature: selectedModel.default_temperature.toString(),
            }));
            setPreviousModel(formData.llm_model);
            return;
        }

        // Model selected but no max_tokens set - set default
        if (!formData.llm_max_tokens) {
            setFormData((prev) => ({
                ...prev,
                llm_max_tokens: selectedModel.default_max_tokens.toString(),
            }));
        }

        // Model selected but no temperature set - set default
        if (!formData.llm_temperature) {
            setFormData((prev) => ({
                ...prev,
                llm_temperature: selectedModel.default_temperature.toString(),
            }));
        }
    }, [formData.llm_model, formData.llm_max_tokens, formData.llm_temperature, previousModel, selectedModel]);

    // Check for unsaved changes
    useEffect(() => {
        if (!originalFormData) return;
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        setHasUnsavedChanges(hasChanges);
    }, [formData, originalFormData]);

    const errorPanelIds = useMemo(
        () => getCommandAgentSettingsPanelsWithErrors(errors),
        [errors]
    );

    const { expiredNames: expiredIntegrationNames } = useCommandAgentExpiredIntegrations(agent.id, {
        agent,
    });

    const alertPanels = useMemo(() => {
        const panels = new Set<CommandAgentSettingsPanelId>(errorPanelIds);
        if (!formData.name.trim()) {
            panels.add("general");
        }
        if (expiredIntegrationNames.length > 0) {
            panels.add("tools");
        }
        return panels;
    }, [errorPanelIds, formData.name, expiredIntegrationNames]);

    const updateMutation = useProtectedMutation({
        mutationFn: (data: any) => {
            return patch(`/api/command-team/agents/${agent.id}`, data);
        },
        requiredPermissions: ["UPDATE_COMMAND_AGENT"],
        onSuccess: async (response) => {
            await queryClient.refetchQueries({ queryKey: ["command-agent", agent.id] });
            await queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            await queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });

            const refreshed =
                (response?.data as CommandAgent | undefined) ??
                queryClient.getQueryData<CommandAgent>(["command-agent", agent.id]);

            if (refreshed && llmModelsData?.models) {
                const synced = buildCommandAgentFormState(refreshed, llmModelsData.models);
                setFormData(synced);
                setOriginalFormData(cloneCommandAgentFormState(synced));
                setPreviousModel(synced.llm_model);
            } else {
                setOriginalFormData(cloneCommandAgentFormState(formData));
            }

            setHasUnsavedChanges(false);
            setFocusedDataTableId(null);
            toast.success("Agent updated", {
                description: `${formData.name.trim() || agent.name} was saved successfully.`,
            });
        },
        onError: (error: any) => {
            if (error?.message === "PERMISSION_DENIED") {
                return;
            }

            const newErrors: FormErrors = {
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                llm_provider: "",
                llm_model: "",
                llm_timeout: "",
                llm_temperature: "",
                llm_max_tokens: "",
                llm_max_completion_tokens: "",
                app_integrations: "",
                custom_apps: "",
                datasources: "",
                data_table_integrations: "",
                visibility: "",
                shared_roles: "",
            };

            const detail = error.response?.data?.detail;
            const apiMessage = error.response?.data?.message;

            if (Array.isArray(detail)) {
                detail.forEach((err: any) => {
                    const field = err.loc[1];
                    const message = err.msg;

                    if (field in newErrors) {
                        newErrors[field as keyof FormErrors] = message;
                    }
                });

                setErrors(newErrors);

                const panels = getCommandAgentSettingsPanelsWithErrors(newErrors);
                if (panels[0]) {
                    setPanel(panels[0]);
                }
            } else if (typeof detail === "string") {
                toast.error("Failed to update command agent", {
                    description: detail,
                });
            } else {
                toast.error("Failed to update command agent", {
                    description:
                        (typeof apiMessage === "string" && apiMessage) ||
                        "An unexpected error occurred",
                });
            }
        },
    });

    const handleInputChange = (field: string, value: string | number | string[] | boolean) => {
        setFormData((prev) => ({
            ...prev,
            [field]:
                typeof value === "boolean"
                    ? value
                    : Array.isArray(value)
                      ? value
                      : value.toString(),
        }));

        if (errors[field as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
        setHasUnsavedChanges(true);
    };

    const handleProviderChange = (provider: string) => {
        setFormData((prev) => applyLlmProviderChange(prev, provider, llmModelsData?.models));
        setPreviousModel(getDefaultModelForProvider(llmModelsData?.models, provider)?.model_key ?? "");
        if (errors.llm_provider) {
            setErrors((prev) => ({ ...prev, llm_provider: "" }));
        }
        if (errors.llm_model) {
            setErrors((prev) => ({ ...prev, llm_model: "" }));
        }
        setHasUnsavedChanges(true);
    };

    const handleIntegrationIdsChange = (integrationIds: string[]) => {
        setFormData((prev) => ({
            ...prev,
            app_integrations: integrationIds,
        }));
        setErrors((prev) => ({
            ...prev,
            app_integrations: "",
        }));
        setHasUnsavedChanges(true);
    };

    const handleCustomAppIdsChange = (customAppIds: string[]) => {
        setFormData((prev) => ({
            ...prev,
            custom_apps: customAppIds,
        }));
        setErrors((prev) => ({
            ...prev,
            custom_apps: "",
        }));
        setHasUnsavedChanges(true);
    };

    const handleDataTableIntegrationsChange = (
        integrations: DataTableIntegration[]
    ) => {
        setFormData((prev) => ({
            ...prev,
            data_table_integrations: integrations,
        }));
        setErrors((prev) => ({
            ...prev,
            data_table_integrations: "",
        }));
        setHasUnsavedChanges(true);
    };

    const handleDataTableIntegrationsSelect = (selectedIds: string[]) => {
        setFormData((prev) => {
            const previousIds = new Set(
                prev.data_table_integrations.map(
                    (integration) => integration.data_table_id
                )
            );
            const newlyAddedId =
                selectedIds.find((id) => !previousIds.has(id)) ?? null;

            if (newlyAddedId) {
                setFocusedDataTableId(newlyAddedId);
            }

            return {
                ...prev,
                data_table_integrations: mergeDataTableIntegrationsSelection(
                    selectedIds,
                    prev.data_table_integrations,
                    agent.data_table_integrations
                ),
            };
        });
        setErrors((prev) => ({
            ...prev,
            data_table_integrations: "",
        }));
        setHasUnsavedChanges(true);
    };

    const handleSave = () => {
        // Only send changed fields
        const changedFields: Record<string, any> = {};

        if (!originalFormData) return;

        // Check each field and add to changedFields if different
        Object.keys(formData).forEach((key) => {
            if (isSystemAgent && (key === "visibility" || key === "shared_roles")) {
                return;
            }

            const currentValue = formData[key as keyof typeof formData];
            const originalValue = originalFormData[key as keyof typeof originalFormData];

            let isEqual: boolean;
            if (key === "shared_roles") {
                isEqual = sameRoleIdLists(
                    (currentValue as string[]) ?? [],
                    (originalValue as string[]) ?? []
                );
            } else if (key === "data_table_integrations") {
                isEqual =
                    normalizeDataTableIntegrationsForCompare(
                        (currentValue as DataTableIntegration[]) ?? []
                    ) ===
                    normalizeDataTableIntegrationsForCompare(
                        (originalValue as DataTableIntegration[]) ?? []
                    );
            } else if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
                isEqual =
                    JSON.stringify([...(currentValue as string[])].sort()) ===
                    JSON.stringify([...(originalValue as string[])].sort());
            } else {
                isEqual = currentValue === originalValue;
            }

            if (!isEqual) {
                // Handle special cases
                if (key === "description" || key === "restrictions") {
                    changedFields[key] = typeof currentValue === "string" && currentValue.trim() === "" ? null : currentValue;
                } else if (key === "llm_timeout" || key === "llm_max_tokens") {
                    changedFields[key] = currentValue ? parseInt(currentValue as string) : null;
                } else if (key === "llm_temperature") {
                    changedFields[key] = currentValue ? parseFloat(currentValue as string) : null;
                } else if (key === "llm_provider" || key === "llm_model") {
                    changedFields[key] = currentValue || null;
                } else if (
                    key === "app_integrations" ||
                    key === "custom_apps" ||
                    key === "datasources" ||
                    key === "data_table_integrations"
                ) {
                    changedFields[key] =
                        key === "data_table_integrations"
                            ? sanitizeDataTableIntegrations(
                                  currentValue as DataTableIntegration[]
                              )
                            : currentValue;
                } else {
                    changedFields[key] = currentValue;
                }
            }
        });

        // If model changed, also send the updated max_tokens
        if (formData.llm_model !== originalFormData.llm_model && selectedModel) {
            changedFields.llm_max_tokens = selectedModel.default_max_tokens;
            // If model has fixed temperature, update it
            if (selectedModel.temperature_min === selectedModel.temperature_max) {
                changedFields.llm_temperature = selectedModel.default_temperature;
            }
        }

        if (Object.keys(changedFields).length === 0) {
            toast.info("No changes to save");
            return;
        }

        setErrors({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            llm_provider: "",
            llm_model: "",
            llm_timeout: "",
            llm_temperature: "",
            llm_max_tokens: "",
            llm_max_completion_tokens: "",
            app_integrations: "",
            custom_apps: "",
            datasources: "",
            data_table_integrations: "",
            visibility: "",
            shared_roles: "",
        });

        updateMutation.mutate(changedFields);
    };

    const handleSubmit = (event?: React.FormEvent) => {
        event?.preventDefault();
        handleSave();
    };

    const handleUndo = () => {
        if (originalFormData) {
            setFormData({ ...originalFormData });
            setPreviousModel(originalFormData.llm_model);
            setHasUnsavedChanges(false);
            setFocusedDataTableId(null);
            setErrors({
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                llm_provider: "",
                llm_model: "",
                llm_timeout: "",
                llm_temperature: "",
                llm_max_tokens: "",
                llm_max_completion_tokens: "",
                app_integrations: "",
                custom_apps: "",
                datasources: "",
                data_table_integrations: "",
                visibility: "",
                shared_roles: "",
            });
            toast.success("Changes reverted");
        }
    };

    if (!agent || !agent.id) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Loading agent details...</p>
            </div>
        );
    }

    const navGroups: SettingsNavGroup[] = [
        {
            id: "agent",
            label: "Agent settings",
            items: [
                { id: "general", label: "General", icon: Bot },
                { id: "behaviour", label: "Behaviour", icon: Brain },
            ],
        },
        {
            id: "advanced",
            label: "Advanced",
            items: [
                { id: "tools", label: "Tools & Integrations", icon: Wrench },
                { id: "knowledge-base", label: "Knowledge Base", icon: BookOpen },
                { id: "llm", label: "LLM Configuration", icon: Cpu },
            ],
        },
    ];

    const temperatureValue = formData.llm_temperature ? parseFloat(formData.llm_temperature) : (selectedModel?.default_temperature ?? 0.0);
    const temperatureMin = selectedModel?.temperature_min ?? 0.0;
    const temperatureMax = selectedModel?.temperature_max ?? 2.0;
    const isTemperatureFixed = selectedModel && selectedModel.temperature_min === selectedModel.temperature_max;


    return (
        <form onSubmit={handleSubmit} className="relative">
            {expiredIntegrationNames.length > 0 ? (
                <div className="mb-5">
                    <CommandV2ExpiredConnectionsBanner
                        expiredNames={expiredIntegrationNames}
                        onReconnect={() => setPanel("tools")}
                    />
                </div>
            ) : null}
            <div className="flex flex-col gap-5 pb-4 lg:flex-row lg:items-start lg:gap-8 lg:pb-6">
                <SettingsPanelNav
                    groups={navGroups}
                    activeId={panel}
                    onSelect={(id) => setPanel(id as CommandAgentSettingsPanelId)}
                    errorIds={[...alertPanels]}
                    className="lg:top-0 lg:space-y-8 [&>div]:space-y-2 [&_button]:py-2.5 [&_p]:mb-1"
                />

                <div className="relative min-w-0 flex-1 space-y-6 pb-20">
                {panel === "general" && (
                    <SettingsPanelCard
                        icon={Bot}
                        title="General"
                        description="Who this digital worker is."
                    >
                        <div className="space-y-3">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <FormField
                                    label="Agent Name"
                                    error={errors.name}
                                    description="Enter a unique name for your command agent"
                                >
                                    <Input
                                        value={formData.name}
                                        onChange={(event) => handleInputChange("name", event.target.value)}
                                        placeholder="e.g., Customer Support Agent"
                                        className={errors.name ? "border-destructive" : ""}
                                    />
                                </FormField>
                                <FormField
                                    label="Role"
                                    error={errors.role}
                                    description="Specify the role of this agent"
                                >
                                    <Input
                                        value={formData.role}
                                        onChange={(event) => handleInputChange("role", event.target.value)}
                                        placeholder="e.g., Customer Support Specialist"
                                        className={errors.role ? "border-destructive" : ""}
                                    />
                                </FormField>
                            </div>
                            <FormField
                                label="Description"
                                error={errors.description}
                                description="Brief description of the agent's purpose"
                            >
                                <Textarea
                                    value={formData.description}
                                    onChange={(event) => handleInputChange("description", event.target.value)}
                                    placeholder="Command agent for customer support tasks."
                                    rows={3}
                                    className={errors.description ? "border-destructive" : ""}
                                />
                            </FormField>
                            {!isSystemAgent ? (
                                <div className="space-y-3 border-t border-border/60 pt-4">
                                    <div>
                                        <p className="text-sm font-semibold">Visibility & sharing</p>
                                        <p className="text-sm text-muted-foreground">
                                            Control who can access this agent across your organisation.
                                        </p>
                                    </div>
                                    <CommandAgentSharingCard
                                        idPrefix="command-agent-settings-tab"
                                        visibility={formData.visibility}
                                        sharedRoles={formData.shared_roles}
                                        canListRoles={canListRoles}
                                        rolesLoading={rolesLoading}
                                        roleOptions={roleOptions}
                                        embedded
                                        errors={{
                                            visibility: errors.visibility,
                                            shared_roles: errors.shared_roles,
                                        }}
                                        onVisibilityChange={(visibility) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                visibility,
                                                shared_roles: visibility === "TEAM" ? prev.shared_roles : [],
                                            }));
                                            setErrors((prev) => ({
                                                ...prev,
                                                visibility: "",
                                                shared_roles: "",
                                            }));
                                            setHasUnsavedChanges(true);
                                        }}
                                        onSharedRolesChange={(value) => {
                                            setFormData((prev) => ({ ...prev, shared_roles: value }));
                                            setErrors((prev) => ({ ...prev, shared_roles: "" }));
                                            setHasUnsavedChanges(true);
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </SettingsPanelCard>
                )}

                {panel === "behaviour" && (
                    <SettingsPanelCard
                        icon={Brain}
                        title="Behaviour"
                        description="Its persona on every reply — and the lines it never crosses."
                    >
                        <div className="space-y-5">
                            <FormField
                                label="Persona"
                                error={errors.persona}
                                description="Define the agent's personality and expertise"
                            >
                                <Textarea
                                    value={formData.persona}
                                    onChange={(event) => handleInputChange("persona", event.target.value)}
                                    placeholder="An expert in handling customer support queries and providing helpful responses."
                                    rows={8}
                                    className={`resize-y ${errors.persona ? "border-destructive" : ""}`}
                                />
                            </FormField>
                            <FormField
                                label="Restrictions"
                                error={errors.restrictions}
                                description="Specify what the agent should not do or avoid"
                            >
                                <Textarea
                                    value={formData.restrictions}
                                    onChange={(event) => handleInputChange("restrictions", event.target.value)}
                                    placeholder="Do not provide financial advice or make promises."
                                    rows={6}
                                    className={`resize-y ${errors.restrictions ? "border-destructive" : ""}`}
                                />
                            </FormField>
                        </div>
                    </SettingsPanelCard>
                )}

                {panel === "tools" && (
                    <div className="space-y-6">
                        <Card className="shadow-sm">
                            <CardHeader className="border-b px-6 py-4">
                                <div className="space-y-1">
                                    <CardTitle>Connected apps</CardTitle>
                                    <CardDescription>
                                        Connect third-party apps to enhance your command agent
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6 p-6">
                                <FormField
                                    label="Connected apps"
                                    error={errors.app_integrations}
                                    description="Select integrations this command agent can use"
                                >
                                    <AppList
                                        variant="integrations"
                                        defaultIntegrationIds={agentConnectionIds.app_integrations}
                                        onIntegrationIdsChange={handleIntegrationIdsChange}
                                        connectedIntegrations={agent.app_integrations || []}
                                        className={errors.app_integrations ? "border-destructive" : ""}
                                    />
                                </FormField>
                                <ConnectedAppsActions
                                    appIntegrations={agent.app_integrations || ([] as any[])}
                                    customApps={[]}
                                />
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm">
                            <CardHeader className="border-b px-6 py-4">
                                <div className="space-y-1">
                                    <CardTitle>Custom apps</CardTitle>
                                    <CardDescription>
                                        Connect MCP or API custom apps for this agent
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6 p-6">
                                <FormField
                                    label="Custom apps"
                                    error={errors.custom_apps}
                                    description="Select custom apps this command agent can use"
                                >
                                    <AppList
                                        variant="custom_apps"
                                        defaultCustomAppIds={agentConnectionIds.custom_apps}
                                        onCustomAppIdsChange={handleCustomAppIdsChange}
                                        connectedCustomApps={agent?.custom_apps || []}
                                        className={errors.custom_apps ? "border-destructive" : ""}
                                    />
                                </FormField>
                                <ConnectedAppsActions
                                    appIntegrations={[]}
                                    customApps={(agent as any)?.custom_apps || []}
                                />
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm">
                            <CardHeader className="border-b px-6 py-4">
                                <div className="space-y-1">
                                    <CardTitle>Knowledge bases</CardTitle>
                                    <CardDescription>
                                        Attach knowledge bases this agent can draw on when answering
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6 p-6">
                                <FormField
                                    label="Knowledge bases"
                                    error={errors.datasources}
                                    description="Select knowledge bases that contain relevant information for this agent"
                                >
                                    <DataSourcesList
                                        key={`datasources-list-${agent.id}`}
                                        onDataSourcesSelect={(dataSourceIds) =>
                                            handleInputChange("datasources", dataSourceIds)
                                        }
                                        defaultValue={formData.datasources}
                                        connectedIds={originalFormData?.datasources ?? []}
                                        className={errors.datasources ? "border-destructive" : ""}
                                    />
                                </FormField>
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm" data-card="data-tables">
                            <CardHeader className="border-b px-6 py-4">
                                <div className="space-y-1">
                                    <CardTitle>Data Tables</CardTitle>
                                    <CardDescription>
                                        Connect data tables that the agent can read from and write
                                        to during conversations
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6 p-6">
                                <FormField
                                    label="Data Tables"
                                    error={errors.data_table_integrations}
                                    description="Select data tables that contain structured records for this command agent. These will help the agent read and manage table data during interactions."
                                >
                                    <DataTableIntegrationSelector
                                        defaultValue={formData.data_table_integrations.map(
                                            (integration) => integration.data_table_id
                                        )}
                                        onIntegrationsSelect={handleDataTableIntegrationsSelect}
                                        disabled={updateMutation.isPending}
                                        className={
                                            errors.data_table_integrations
                                                ? "border-destructive"
                                                : ""
                                        }
                                    />
                                </FormField>
                                <ConnectedDataTableIntegrations
                                    integrations={formData.data_table_integrations}
                                    savedIntegrationIds={getDataTableIdsFromAgent}
                                    disabled={updateMutation.isPending}
                                    onChange={handleDataTableIntegrationsChange}
                                    focusedDataTableId={focusedDataTableId}
                                    onFocusComplete={() => setFocusedDataTableId(null)}
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}

                {panel === "knowledge-base" && (
                    <AgentKnowledgeBaseTab
                        className="py-0"
                        datasources={agent.datasources ?? []}
                    />
                )}

                {panel === "llm" && (
                    <SettingsPanelCard
                        icon={Cpu}
                        title="LLM configuration"
                        description="Model and generation limits for this agent's replies."
                    >
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <FormField
                                    label="LLM Provider"
                                    error={errors.llm_provider}
                                    description="Select the AI provider"
                                >
                                    <Select
                                        value={formData.llm_provider || "OPENAI"}
                                        onValueChange={handleProviderChange}
                                    >
                                        <SelectTrigger className={errors.llm_provider ? "border-destructive" : ""}>
                                            <SelectValue placeholder="Select a provider" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="OPENAI">OpenAI</SelectItem>
                                            <SelectItem value="ANTHROPIC">Anthropic</SelectItem>
                                            <SelectItem value="MOONSHOTAI">MoonshotAI</SelectItem>
                                            <SelectItem value="ZHIPU">Zhipu</SelectItem>
                                            <SelectItem value="DEEPSEEK">DeepSeek</SelectItem>
                                            <SelectItem value="META">Meta</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField
                                    label="LLM Model"
                                    error={errors.llm_model}
                                    description="Select the AI model"
                                >
                                    <Select
                                        key={`model-${formData.llm_provider}-${formData.llm_model || "empty"}`}
                                        value={formData.llm_model || undefined}
                                        onValueChange={(value) => handleInputChange("llm_model", value)}
                                        disabled={isLoadingModels}
                                    >
                                        <SelectTrigger className={errors.llm_model ? "border-destructive" : ""}>
                                            <SelectValue
                                                placeholder={
                                                    isLoadingModels ? "Loading models..." : "Select a model"
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {filteredModels.map((model) => (
                                                <SelectItem key={model.model_id} value={model.model_key}>
                                                    {model.display_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <Timer className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <div className="font-semibold">Temperature</div>
                                        <div className="text-xs text-muted-foreground">
                                            {selectedModel
                                                ? isTemperatureFixed
                                                    ? "This model has a fixed temperature"
                                                    : `Controls randomness (${temperatureMin} = deterministic, ${temperatureMax} = very random)`
                                                : "Controls randomness in responses (0.0-2.0)"}
                                        </div>
                                    </div>
                                </div>
                                {selectedModel ? (
                                    <VoiceSlider
                                        value={[temperatureValue]}
                                        onChange={(value) => handleInputChange("llm_temperature", value[0])}
                                        min={temperatureMin}
                                        max={temperatureMax}
                                        step={0.1}
                                        unit=""
                                        heading=""
                                        subheading=""
                                        showValue={true}
                                    />
                                ) : (
                                    <VoiceSlider
                                        value={[formData.llm_temperature ? parseFloat(formData.llm_temperature) : 0]}
                                        onChange={(value) => handleInputChange("llm_temperature", value[0])}
                                        min={0}
                                        max={2}
                                        step={0.1}
                                        unit=""
                                        heading=""
                                        subheading=""
                                        showValue={true}
                                    />
                                )}
                                {errors.llm_temperature ? (
                                    <p className="text-sm text-destructive">{errors.llm_temperature}</p>
                                ) : null}
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <Timer className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <div className="font-semibold">Timeout</div>
                                        <div className="text-xs text-muted-foreground">
                                            Maximum time to wait for AI response (10-300 seconds)
                                        </div>
                                    </div>
                                </div>
                                <VoiceSlider
                                    value={[formData.llm_timeout ? parseInt(formData.llm_timeout) : 10]}
                                    onChange={(value) => handleInputChange("llm_timeout", value[0])}
                                    min={10}
                                    max={300}
                                    step={1}
                                    unit="sec"
                                    heading=""
                                    subheading=""
                                    showValue={true}
                                />
                                {errors.llm_timeout ? (
                                    <p className="text-sm text-destructive">{errors.llm_timeout}</p>
                                ) : null}
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <Timer className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <div className="font-semibold">Max Tokens</div>
                                        <div className="text-xs text-muted-foreground">
                                            {selectedModel
                                                ? `Maximum tokens in the response (${selectedModel.max_tokens_min}-${selectedModel.max_tokens_max})`
                                                : "Maximum tokens in the response (select a model to see limits)"}
                                        </div>
                                    </div>
                                </div>
                                {selectedModel ? (
                                    <VoiceSlider
                                        value={[
                                            formData.llm_max_tokens
                                                ? parseInt(formData.llm_max_tokens)
                                                : selectedModel.default_max_tokens,
                                        ]}
                                        onChange={(value) => handleInputChange("llm_max_tokens", value[0])}
                                        min={selectedModel.max_tokens_min}
                                        max={selectedModel.max_tokens_max}
                                        step={1}
                                        unit=""
                                        heading=""
                                        subheading=""
                                        showValue={true}
                                    />
                                ) : (
                                    <div className="rounded-xl bg-muted p-3">
                                        <p className="text-center text-sm text-muted-foreground">
                                            Please select a model to configure max tokens
                                        </p>
                                    </div>
                                )}
                                {errors.llm_max_tokens ? (
                                    <p className="text-sm text-destructive">{errors.llm_max_tokens}</p>
                                ) : null}
                            </div>
                        </div>
                    </SettingsPanelCard>
                )}

                    {panel !== "knowledge-base" ? (
                        <PermissionGate requiredPermissions={["UPDATE_COMMAND_AGENT"]}>
                            <CommandAgentSettingsActionBar
                                hasUnsavedChanges={hasUnsavedChanges}
                                isSaving={updateMutation.isPending}
                                nameMissing={!formData.name.trim()}
                                onDiscard={handleUndo}
                                onSave={handleSave}
                                onGoToGeneral={() => setPanel("general")}
                            />
                        </PermissionGate>
                    ) : null}
                </div>
            </div>
        </form>
    );
}
